// Canvas-side postMessage endpoint. Lets peer Symbols origins iframe
// this page and read the SDK tokens held in canvas's localStorage so a
// user signed in on canvas doesn't have to sign in again on workspace /
// marketplace / preview. Whitelisted origins only; page has no UI.

// Imported through the shared package's DISCOVERED BAG, not as a named
// import from a deep file path. A named deep import emits as an
// `import_crossAppAuth.collectSdkTokens` alias that frank's emitted
// `onRender` cannot resolve (unresolvable-identifier — a guaranteed
// ReferenceError in the published payload). frank rewrites the bag import
// inside a handler into a runtime lookup of the shared package — the same
// idiom the workspace shell's own session-bridge page uses.
import _wsShared from '@symbo-ls/workspace-shared'

export const sessionBridge = {
  metadata: (el) => ({ title: el.call('polyglot', 'canvas.page.sessionBridge.title') }),
  // Fixed near-black regardless of theme — this is a headless postMessage
  // bridge with no visible UI (mirrors the workspace-side session-bridge).
  background: '#0f1116',
  // Raw rgba(255,255,255,0.35) measured 3.20:1 on this ground (needs 4.5) —
  // fable.md AA sweep. white.55 clears the floor with real margin (~6.22:1,
  // matching the sibling workspace-side session-bridge fix) while staying
  // visibly dimmer than a normal caption.
  color: 'white.55',
  fontFamily: 'system-ui',
  fontSize: 'Z2',
  padding: 'A',
  text: '{{ canvas.sessionBridge.body | polyglot }}',

  // The handshake listener is DECLARED, not armed imperatively (FA503).
  // It used to be a `window.addEventListener('message', …)` fired from
  // `onRender` behind an `el.__bridgeInit` latch with no removal anywhere —
  // so every mount of this page added one more permanent window listener,
  // each holding this element (and its whole subtree) reachable from
  // `window` forever. DOMQL registers this once when the element takes its
  // node and tears it down in dispose(); the latch is gone with it.
  //
  // The registration moved; the POLICY did not, and it lives in ONE place —
  // shared functions/sessionBridgeResponder.js, which the workspace shell's
  // page and its light bridge entry answer with too: a foreign origin is
  // rejected before anything is read, a message of the wrong type is ignored,
  // and the answer is the ACCESS token + expiry only, never the refresh token
  // (the server rotates refresh tokens, so a copy on the peer origin signed one
  // of the two origins out on every rotation). The SDK (when this page has
  // one) is reached only when this origin's own token must rotate first, so
  // the peer never gets a dying token.
  onWindowMessage: (ev, el) =>
    _wsShared.functions.answerSessionBridgeRequest(ev, { getSdk: () => el?.call?.('getSDK') }),

  onRender: (el) => {
    if (el.__bridgeReady) return
    el.__bridgeReady = true

    // Parent waits for this before sending its request so the handshake
    // can't race a listener that armed after postMessage. The declared
    // `onWindowMessage` above registers with the element's node — which the
    // framework takes BEFORE it runs onRender — so the listener is already
    // live when this ready-ping goes out.
    _wsShared.functions.announceSessionBridgeReady()
  }
}
